import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardService from '../services/dashboardService';
import TaskCompletionModal from '../components/TaskCompletionModal';
import VolunteerNav from '../components/VolunteerNav';
import NotificationBanner from '../components/NotificationBanner';
import AnnouncementsBanner from '../components/AnnouncementsBanner';
import api from '../services/api';
import Icons from '../components/Icons';

const VolunteerDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calendarView, setCalendarView] = useState('month'); // 'month' or 'list'

  // Task completion modal state
  const [completionModal, setCompletionModal] = useState({
    isOpen: false,
    taskId: null,
    taskTitle: '',
    loading: false
  });

  // Event modal state
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState(false);

  // Document preview modal state
  const [documentPreview, setDocumentPreview] = useState({
    isOpen: false,
    document: null,
    loading: false,
    previewUrl: null
  });
  const [passwordCopied, setPasswordCopied] = useState(false);

  // Load dashboard data
  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);
        const result = await DashboardService.getVolunteerDashboard();
        setDashboardData(result.data);
      } catch (error) {
        console.error('Dashboard error:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const loadAnnouncements = async () => {
      try {
        const result = await DashboardService.getAnnouncements();
        setAnnouncements(result.announcements || []);
      } catch (error) {
        // Non-critical - dashboard should still work if announcements fail to load
        console.warn('Announcements unavailable:', error.message);
      }
    };

    loadDashboard();
    loadAnnouncements();
  }, []);

  // Handle marking an announcement as read
  const handleMarkAnnouncementRead = async (announcementId) => {
    try {
      await DashboardService.markAnnouncementRead(announcementId);
    } catch (error) {
      console.error('Mark announcement read error:', error);
    }
  };

  // Handle task status update
  const handleTaskStatusUpdate = async (taskId, newStatus, completionNotes = null) => {
    try {
      setCompletionModal(prev => ({ ...prev, loading: true }));

      await DashboardService.updateTaskStatus(taskId, newStatus, completionNotes);

      // Reload dashboard data to reflect changes
      const result = await DashboardService.getVolunteerDashboard();
      setDashboardData(result.data);

      // Close modal and reset state
      setCompletionModal({
        isOpen: false,
        taskId: null,
        taskTitle: '',
        loading: false
      });
    } catch (error) {
      console.error('Task update error:', error);
      setCompletionModal(prev => ({ ...prev, loading: false }));
      setError(`Failed to update task: ${error.message}`);
    }
  };

  // Open completion modal
  const openCompletionModal = (taskId, taskTitle) => {
    setCompletionModal({
      isOpen: true,
      taskId,
      taskTitle,
      loading: false
    });
  };

  // Close completion modal
  const closeCompletionModal = () => {
    setCompletionModal({
      isOpen: false,
      taskId: null,
      taskTitle: '',
      loading: false
    });
  };

  // Handle completion confirmation
  const handleCompletionConfirm = (notes) => {
    if (completionModal.taskId) {
      handleTaskStatusUpdate(completionModal.taskId, 'completed', notes);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    // Parse date as local time to avoid timezone shifts
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Handle event click - fetch detailed event info and show modal
  const handleEventClick = async (event) => {
    try {
      const response = await api.get(`/calendar/events/${event.id}`);

      if (response.data.success) {
        setSelectedEvent(response.data.event);
        setShowEventModal(true);
      } else {
        setError(response.data.message || 'Failed to load event details');
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
      setError('Failed to load event details');
    }
  };

  // Handle RSVP to event
  const handleRSVP = async (eventId, status) => {
    try {
      setRsvpLoading(true);
      const response = await api.post(`/calendar/events/${eventId}/rsvp`, {
        status: status
      });

      if (response.data.success) {
        // Reload dashboard data to reflect changes
        const result = await DashboardService.getVolunteerDashboard();
        setDashboardData(result.data);

        // Refresh the selected event details
        if (selectedEvent) {
          handleEventClick(selectedEvent);
        }
      } else {
        setError(response.data.message || 'Failed to update RSVP');
      }
    } catch (error) {
      console.error('Error updating RSVP:', error);
      setError('Failed to update RSVP');
    } finally {
      setRsvpLoading(false);
    }
  };

  const buildGoogleCalendarUrl = (event) => {
    const pad = (n) => String(n).padStart(2, '0');
    const toGoogleDate = (dateStr, timeStr) => {
      const [y, m, d] = dateStr.split('T')[0].split('-');
      if (!timeStr || event.is_all_day) return `${y}${m}${d}`;
      const [h, min] = timeStr.split(':');
      return `${y}${m}${d}T${pad(h)}${pad(min)}00`;
    };
    const start = toGoogleDate(event.start_date, event.start_time);
    const endDate = event.end_date || event.start_date;
    let end = toGoogleDate(endDate, event.end_time || event.start_time);
    if (event.is_all_day) {
      const d = new Date(endDate.split('T')[0]);
      d.setDate(d.getDate() + 1);
      end = `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}`;
    }
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title || '',
      dates: `${start}/${end}`,
      details: event.description || '',
      location: event.location || '',
    });
    return `https://calendar.google.com/calendar/render?${params}`;
  };

  const buildOutlookUrl = (event) => {
    const toISOLocal = (dateStr, timeStr) => {
      const d = dateStr.split('T')[0];
      if (!timeStr || event.is_all_day) return `${d}`;
      return `${d}T${timeStr.slice(0,5)}:00`;
    };
    const params = new URLSearchParams({
      rru: 'addevent',
      startdt: toISOLocal(event.start_date, event.start_time),
      enddt: toISOLocal(event.end_date || event.start_date, event.end_time || event.start_time),
      subject: event.title || '',
      body: event.description || '',
      location: event.location || '',
    });
    return `https://outlook.live.com/calendar/0/deeplink/compose?${params}`;
  };

  // Export event to calendar
  const exportEvent = async (eventId) => {
    try {
      // Detect user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await api.get(`/calendar/events/${eventId}/export`, {
        params: { timezone: userTimezone },
        responseType: 'blob'
      });

      // Create a blob from the response
      const blob = new Blob([response.data], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `event-${eventId}.ics`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting event:', error);
      setError('Failed to export event');
    }
  };


  // Handle document preview
  const handlePreviewDocument = async (doc) => {
    if (doc.document_type === 'embedded') {
      setDocumentPreview({ isOpen: true, document: doc, loading: false, previewUrl: null });
      return;
    }
    setDocumentPreview({ isOpen: true, document: doc, loading: true, previewUrl: null });
    try {
      const response = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: doc.file_type });
      const previewUrl = window.URL.createObjectURL(blob);
      setDocumentPreview(prev => ({ ...prev, loading: false, previewUrl }));
    } catch (error) {
      console.error('Error generating preview:', error);
      setDocumentPreview(prev => ({ ...prev, loading: false, previewUrl: null }));
    }
  };

  const closeDocumentPreview = () => {
    if (documentPreview.previewUrl) {
      window.URL.revokeObjectURL(documentPreview.previewUrl);
    }
    setDocumentPreview({ isOpen: false, document: null, loading: false, previewUrl: null });
  };

  const convertToEmbedUrl = (url) => {
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname.includes('sharepoint.com')) {
        urlObj.searchParams.set('action', 'embedview');
        return urlObj.toString();
      }
      if (urlObj.hostname.includes('onedrive.live.com')) {
        urlObj.pathname = urlObj.pathname.replace('/redir', '/embed').replace('/view', '/embed');
        return urlObj.toString();
      }
      if (urlObj.hostname.includes('docs.google.com') && urlObj.pathname.includes('/spreadsheets/')) {
        return url.replace(/\/(edit|view)(#.*)?(\?.*)?$/, '/pubhtml');
      }
      if (urlObj.hostname.includes('docs.google.com') && urlObj.pathname.includes('/document/')) {
        return url.replace(/\/(edit|view)(#.*)?(\?.*)?$/, '/pub?embedded=true');
      }
      if (urlObj.hostname.includes('docs.google.com') && urlObj.pathname.includes('/presentation/')) {
        return url.replace(/\/(edit|view)(#.*)?(\?.*)?$/, '/embed');
      }
      if (urlObj.hostname.includes('drive.google.com') && urlObj.pathname.includes('/file/')) {
        const fileId = urlObj.pathname.match(/\/d\/([^/]+)/)?.[1];
        if (fileId) return `https://drive.google.com/file/d/${fileId}/preview`;
      }
      return url;
    } catch {
      return url;
    }
  };

  const handleCopyPassword = (password) => {
    navigator.clipboard.writeText(password);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  // Get urgency styling based on due date
  const getUrgencyStyle = (task) => {
    // No color scheme for completed tasks
    if (task.assignment_status === 'completed') return '';

    if (!task.due_date) return '';

    // Parse date as local time to avoid timezone shifts
    const [year, month, day] = task.due_date.split('T')[0].split('-');
    const dueDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysDiff = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

    if (daysDiff < 0) return 'border-l-4 border-l-red-500 bg-red-50'; // Overdue
    if (daysDiff === 0) return 'border-l-4 border-l-orange-500 bg-orange-50'; // Due today
    if (daysDiff <= 3) return 'border-l-4 border-l-yellow-500 bg-yellow-50'; // Due soon
    return 'border-l-4 border-l-blue-500 bg-blue-50'; // Normal
  };

  // Get task status color - simplified model
  const getTaskStatusColor = (status) => {
    switch (status) {
      case 'assigned': return 'bg-blue-50 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-50 text-green-800 border-green-200';
      default: return 'bg-gray-50 text-gray-800 border-gray-200';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          {Icons.alertTriangle("w-12 h-12 text-red-500 mx-auto mb-4")}
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const { tasks, events, documents, taskStats } = dashboardData || {};

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <VolunteerNav currentPage="Dashboard" />

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pt-16 lg:pt-0 h-screen">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.firstName || 'Volunteer'}!
          </h2>
          <p className="text-gray-600">
            Here's what's happening in your organization
          </p>
        </div>

        {/* Notification Banner */}
        <NotificationBanner />

        {/* Announcements */}
        <AnnouncementsBanner
          announcements={announcements}
          onMarkRead={handleMarkAnnouncementRead}
        />

        {/* Quick Stats - Simplified Model */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-50">
                <span className="text-blue-600">{Icons.clipboard("w-5 h-5")}</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Tasks</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {taskStats?.total_tasks || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-50">
                <span className="text-yellow-600">{Icons.bell("w-5 h-5")}</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Assigned</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {taskStats?.assigned_tasks || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-50">
                <span className="text-green-600">{Icons.check("w-5 h-5")}</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {taskStats?.completed_tasks || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-50">
                <span className="text-purple-600">{Icons.calendar("w-5 h-5")}</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Upcoming Events</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {events?.length || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Calendar Section */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400">{Icons.calendar("w-4 h-4")}</span>
                  <h3 className="text-lg font-medium text-gray-900">Upcoming Events</h3>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCalendarView('month')}
                    className={`px-3 py-1 text-sm rounded ${
                      calendarView === 'month'
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Month
                  </button>
                  <button
                    onClick={() => setCalendarView('list')}
                    className={`px-3 py-1 text-sm rounded ${
                      calendarView === 'list'
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    List
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              {events && events.length > 0 ? (
                <div className="space-y-4">
                  {events.slice(0, 5).map((event, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                      onClick={() => handleEventClick(event)}
                    >
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                          <span className="text-primary-600 font-medium">
                            {(() => {
                              const [year, month, day] = event.start_date.split('T')[0].split('-');
                              return parseInt(day);
                            })()}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">
                          {event.title}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {formatDate(event.start_date)}
                          {event.start_time && ` at ${formatTime(event.start_time)}`}
                        </p>
                        {event.description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {event.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <span className="flex justify-center text-gray-300 mb-2">{Icons.calendar("w-10 h-10")}</span>
                  <p className="text-gray-600">No upcoming events</p>
                </div>
              )}

              <div className="mt-4 pt-4 border-t">
                <Link
                  to="/calendar"
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  View full calendar →
                </Link>
              </div>
            </div>
          </div>

          {/* My Tasks Section */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <span className="text-gray-400">{Icons.clipboard("w-4 h-4")}</span>
                <h3 className="text-lg font-medium text-gray-900">My Recent Tasks</h3>
              </div>
            </div>

            <div className="p-6">
              {tasks && tasks.length > 0 ? (
                <div className="space-y-4">
                  {tasks.slice(0, 5).map((task, index) => (
                    <div key={index} className={`border rounded-lg p-4 ${getUrgencyStyle(task)}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-2">{task.title}</h4>

                          <div className="flex items-center flex-wrap gap-2 mb-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(task.priority)}`}>
                              {task.priority}
                            </span>
                            <span className={`px-3 py-1 text-xs rounded-full border ${getTaskStatusColor(task.assignment_status)}`}>
                              {task.assignment_status.replace('_', ' ')}
                            </span>
                          </div>

                          {task.description && (
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                              {task.description}
                            </p>
                          )}

                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            {task.due_date && (
                              <span>Due: {formatDate(task.due_date)}</span>
                            )}
                            {task.estimated_hours && (
                              <span>Est: {task.estimated_hours}h</span>
                            )}
                          </div>
                        </div>

                        <div className="ml-4 flex flex-col items-end space-y-2">
                          {task.assignment_status === 'assigned' && (
                            <button
                              onClick={() => openCompletionModal(task.id, task.title)}
                              className="text-xs bg-success-600 text-white px-3 py-1 rounded hover:bg-success-700"
                            >
                              Complete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <span className="flex justify-center text-gray-300 mb-2">{Icons.clipboard("w-10 h-10")}</span>
                  <p className="text-gray-600">No tasks assigned</p>
                </div>
              )}

              <div className="mt-4 pt-4 border-t">
                <Link
                  to="/my-tasks"
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  View all my tasks →
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className="mt-8 bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <span className="text-gray-400">{Icons.doc("w-4 h-4")}</span>
              <h3 className="text-lg font-medium text-gray-900">Recent Documents</h3>
            </div>
          </div>

          <div className="p-6">
            {documents && documents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.slice(0, 6).map((doc, index) => (
                  <div
                    key={index}
                    onClick={() => handlePreviewDocument(doc)}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        {doc.is_pinned ? Icons.pin("w-4 h-4 text-blue-500") : Icons.doc("w-4 h-4 text-gray-400")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900 truncate">
                            {doc.title}
                          </h4>
                          {doc.is_pinned && (
                            <span className="flex-shrink-0 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-medium">
                              Pinned
                            </span>
                          )}
                        </div>
                        {doc.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {doc.description}
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">
                            {formatDate(doc.created_at)}
                          </span>
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {doc.category}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <span className="flex justify-center text-gray-300 mb-2">{Icons.doc("w-10 h-10")}</span>
                <p className="text-gray-600">No documents available</p>
              </div>
            )}

            <div className="mt-6 pt-4 border-t">
              <Link
                to="/documents"
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                View all documents →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Task Completion Modal */}
      <TaskCompletionModal
        isOpen={completionModal.isOpen}
        onClose={closeCompletionModal}
        onConfirm={handleCompletionConfirm}
        taskTitle={completionModal.taskTitle}
        loading={completionModal.loading}
      />

      {/* Event Details Modal */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-2xl w-full my-8">
            <div className="p-6 max-h-[calc(100vh-4rem)] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">{selectedEvent.title}</h2>
                <button
                  onClick={() => setShowEventModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <strong>Date:</strong> {formatDate(selectedEvent.start_date)}
                  {selectedEvent.end_date && selectedEvent.end_date !== selectedEvent.start_date && (
                    <span> - {formatDate(selectedEvent.end_date)}</span>
                  )}
                </div>

                {!selectedEvent.is_all_day && (
                  <div>
                    <strong>Time:</strong> {formatTime(selectedEvent.start_time)}
                    {selectedEvent.end_time && ` - ${formatTime(selectedEvent.end_time)}`}
                  </div>
                )}

                {selectedEvent.location && (
                  <div>
                    <strong>Location:</strong> {selectedEvent.location}
                  </div>
                )}

                {selectedEvent.description && (
                  <div>
                    <strong>Description:</strong>
                    <p className="mt-1 text-gray-700">{selectedEvent.description}</p>
                  </div>
                )}

                <div>
                  <strong>Attendance:</strong> {selectedEvent.confirmed_signups} RSVP'd
                </div>

                {selectedEvent.creator_name && (
                  <div>
                    <strong>Organized by:</strong> {selectedEvent.creator_name} {selectedEvent.creator_lastname}
                  </div>
                )}
              </div>

              {/* RSVP Actions */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row gap-3">
                  {selectedEvent.user_rsvp_status === 'signed_up' ? (
                    // User is signed up - show option to change to No
                    <button
                      onClick={() => handleRSVP(selectedEvent.id, 'cancelled')}
                      disabled={rsvpLoading}
                      className="bg-danger-600 text-white px-4 py-2 rounded-md hover:bg-danger-700 transition-colors disabled:opacity-50"
                    >
                      {rsvpLoading ? 'Updating...' : 'RSVP No'}
                    </button>
                  ) : selectedEvent.user_rsvp_status === 'cancelled' ? (
                    // User has RSVP'd No - allow them to change to Yes regardless of capacity
                    <button
                      onClick={() => handleRSVP(selectedEvent.id, 'signed_up')}
                      disabled={rsvpLoading}
                      className="bg-success-600 text-white px-4 py-2 rounded-md hover:bg-success-700 transition-colors disabled:opacity-50"
                    >
                      {rsvpLoading ? 'RSVPing...' : 'RSVP Yes'}
                    </button>
                  ) : (
                    // User hasn't RSVP'd yet - show both options
                    <>
                      {selectedEvent.can_signup && (
                        <button
                          onClick={() => handleRSVP(selectedEvent.id, 'signed_up')}
                          disabled={rsvpLoading}
                          className="bg-success-600 text-white px-4 py-2 rounded-md hover:bg-success-700 transition-colors disabled:opacity-50"
                        >
                          {rsvpLoading ? 'RSVPing...' : 'RSVP Yes'}
                        </button>
                      )}
                      <button
                        onClick={() => handleRSVP(selectedEvent.id, 'cancelled')}
                        disabled={rsvpLoading}
                        className="bg-danger-600 text-white px-4 py-2 rounded-md hover:bg-danger-700 transition-colors disabled:opacity-50"
                      >
                        {rsvpLoading ? 'Updating...' : 'RSVP No'}
                      </button>
                    </>
                  )}

                  <a
                    href={buildGoogleCalendarUrl(selectedEvent)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium text-center"
                  >
                    + Google Calendar
                  </a>
                  <a
                    href={buildOutlookUrl(selectedEvent)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium text-center"
                  >
                    + Outlook
                  </a>
                  <button
                    onClick={() => exportEvent(selectedEvent.id)}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Download .ics
                  </button>
                </div>

                {!selectedEvent.can_signup && !selectedEvent.user_rsvp_status && (
                  <p className="text-red-600 text-sm mt-2">This event is at capacity</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </main>

      {/* Document Preview Modal */}
      {documentPreview.isOpen && documentPreview.document && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{documentPreview.document.title}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {documentPreview.document.document_type === 'embedded'
                    ? 'Linked Document'
                    : documentPreview.document.file_name || 'Uploaded File'}
                </p>
              </div>
              <button onClick={closeDocumentPreview} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {documentPreview.document.document_type === 'embedded' ? (
                <div>
                  {documentPreview.document.embed_password && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-yellow-800 font-medium mb-2">
                        This document is password-protected. Copy the password below when prompted:
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="bg-white px-3 py-2 rounded border border-yellow-300 text-sm font-mono flex-1 text-gray-900">
                          {documentPreview.document.embed_password}
                        </code>
                        <button
                          onClick={() => handleCopyPassword(documentPreview.document.embed_password)}
                          className={`px-4 py-2 rounded text-sm font-medium transition-colors whitespace-nowrap ${passwordCopied ? 'bg-success-600 text-white' : 'bg-warning-600 hover:bg-warning-700 text-white'}`}
                        >
                          {passwordCopied ? 'Copied!' : 'Copy Password'}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="bg-gray-100 rounded-lg overflow-hidden">
                    <iframe
                      src={convertToEmbedUrl(documentPreview.document.embed_url)}
                      className="w-full border-0"
                      style={{ height: '600px' }}
                      title={documentPreview.document.title}
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                    />
                  </div>
                  <div className="mt-3 text-center">
                    <p className="text-sm text-gray-500 mb-2">If the document doesn't load above, open it directly:</p>
                    <a
                      href={documentPreview.document.embed_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium transition-colors"
                    >
                      Open in New Tab
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  {documentPreview.loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      <p className="ml-4 text-gray-600">Loading preview...</p>
                    </div>
                  ) : documentPreview.previewUrl ? (
                    <div className="bg-gray-100 rounded-lg overflow-hidden">
                      {documentPreview.document.file_type?.includes('pdf') && (
                        <iframe src={documentPreview.previewUrl} className="w-full h-[500px] border-0" title="PDF Preview" />
                      )}
                      {documentPreview.document.file_type?.includes('image') && (
                        <img src={documentPreview.previewUrl} alt={documentPreview.document.title} className="w-full h-auto max-h-[500px] object-contain" />
                      )}
                      {documentPreview.document.file_type &&
                        !documentPreview.document.file_type.includes('pdf') &&
                        !documentPreview.document.file_type.includes('image') && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                          <h4 className="text-blue-800 font-medium mb-2">Preview Not Available</h4>
                          <p className="text-blue-700 text-sm">Please download the document to view its contents.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-blue-800 font-medium">Unable to Load Preview</h4>
                      <p className="text-blue-700 text-sm mt-1">Please go to Documents to download this file.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VolunteerDashboard;